<script>
	import { onMount } from "svelte";

	import SecretHider from "../components/SecretHider.svelte";
	import SpinnerLocked from "../components/SpinnerLocked.svelte";
	import SpinnerUnlock from "../components/SpinnerUnlock.svelte";
	import { minDelay } from "../utils.js";
	import { apiPost } from '../api.js';
	
    let { params } = $props();

	const url = "SECURESHARE_API_URL";
	const api_key = "SECURESHARE_API_KEY";

	let secretId = null;
	let secret = '';
	let password = $state(null);
	let retrievePromise = $state({});
	let retrieveRequested = $state(false);
	let linkValid = $state(null);
	let memo = $state('');
	// Pre-decrypt lifecycle counters (from validate_secret)
	let readCount = $state(null);
	let burnAfterReading = $state(null);
	let remainingReads = $derived((burnAfterReading != null && readCount != null && burnAfterReading > 0)
		? Math.max(0, burnAfterReading - readCount)
		: null);
	let isFinalRead = $derived(remainingReads === 1);

	// Post-decrypt state (from retrieve_sse): includes the already-counted
	// read, so this is the number of reads remaining AFTER this one.
	let postReadRemaining = $state(null);
	let expiresAt = $state(null);
	function formatExpiry(unix) {
		if (!unix) return '';
		return new Date(unix * 1000).toLocaleString([], {
			dateStyle: 'medium',
			timeStyle: 'short',
		});
	}

	let file_id = $state(null);
	let file_name = $state(null);
	let file_url = $state(null);

	async function validateSecret() {
		const res = await apiPost(`${url}/validate_secret`, { id: secretId }, { apiKey: api_key });
		linkValid = res.ok;
		if (res.ok) {
			const json = await res.json();
			readCount = json.read_count;
			burnAfterReading = json.burn_after_reading;
		}
	}

    async function getSecret() {
		const secretRequest = {
			id: secretId,
			password: password
		};

		const res = await apiPost(`${url}/retrieve_sse`, secretRequest, { apiKey: api_key });
		const json = await res.json();
		if (res.ok) {
			secret = json.secret;
			file_id = json.file_id;
			file_name = json.file_name;
			// retrieve_sse now mints the presigned download URL inline,
			// so the separate s3_presigned_retrieve_generator round-trip
			// (and the public-route vuln it carried) is gone.
			file_url = json.file_url;
			memo = json.memo || '';
			postReadRemaining = json.remaining_reads;
			expiresAt = json.expires_at;

			return json.secret;
		} else {
			throw new Error(json);
		}
	}

	function retrieveSecret() {
		retrieveRequested = true;
		retrievePromise = minDelay(getSecret());
	}

	// Downloads are intentionally a plain anchor click, not a fetch() →
	// blob → synthetic-anchor dance. The presigned URL is generated with
	// ResponseContentDisposition: 'attachment;filename*=...' (see
	// python_library/presigned_link_downloads.py), so when the browser
	// navigates to it the response carries `Content-Disposition: attachment`
	// and the browser downloads instead of leaving the page. Top-level
	// navigations aren't subject to CORS, so the bucket only needs the
	// upload (POST) CORS rule — adding GET would broaden the surface for
	// no benefit.

	let passwordSupplied = $derived(password !== null && password.trim().length > 0);

	onMount(() => {
		secretId = params.id;
		validateSecret();
	});
</script>

<main class="container">
	<div class:hidden={!retrieveRequested}>
		{#await retrievePromise}
			<SpinnerUnlock></SpinnerUnlock>
		{:then secretText}
			<div id="secret_container">
				{#if memo}
					<div class="memo" aria-label="Sender's note">
						<span class="memo-label">From the sender:</span>
						<p class="memo-text">{memo}</p>
					</div>
				{/if}
				<SecretHider secret={secretText} mode="textarea"></SecretHider>
				{#if file_id && file_url}
				<div>
					<h3>Attachments</h3>
					<a class="attachment-card" href={file_url}>
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
							<path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
							<path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
						</svg>
						<span class="attachment-name">{file_name}</span>
					</a>
				</div>
				{/if}
				{#if postReadRemaining !== null || expiresAt}
					<dl class="lifecycle">
						{#if postReadRemaining !== null}
							<dt>Reads remaining</dt>
							<dd>
								{postReadRemaining}
								{#if postReadRemaining === 0}
									<span class="destroyed">— this message has now been destroyed</span>
								{/if}
							</dd>
						{/if}
						{#if expiresAt}
							<dt>Expires</dt>
							<dd>{formatExpiry(expiresAt)}</dd>
						{/if}
					</dl>
				{/if}
			</div>
		{:catch error}
			<p style="color: red">Failed to read message.</p>
		{/await}
	</div>
	{#if linkValid === false}
		<div class="alert alert-danger">This link is invalid or has expired.</div>
	{/if}
	<div class="pre-retrieve" class:hidden={retrieveRequested || linkValid !== true}>
		<SpinnerLocked></SpinnerLocked>
		{#if remainingReads !== null}
			{#if isFinalRead}
				<div class="alert alert-warning final-read-warning">
					<strong>This is the final read.</strong> Once you click "Read Message", the message will be permanently destroyed.
				</div>
			{:else}
				<div class="reads-remaining">{remainingReads} reads remaining</div>
			{/if}
		{/if}
        <div class="form-item">
            <label class="form-label" for="password">Password</label>
            <span class="hint">Check your email for the password.</span>
        	<input type="password" bind:value={password} class="form-control password-input" autocomplete="off">
        </div>
        <button onclick={retrieveSecret} disabled={!passwordSupplied} class="btn btn-primary">Read Message</button>
	</div>
</main>

<style>
	#secret_container > :global(textarea) {
		width: 100%;
		height: 400px;
	}

	.attachment-card {
		display: inline-flex;
		align-items: center;
		gap: .75rem;
		padding: .75rem 1.25rem;
		border: 1px solid #dee2e6;
		border-radius: .5rem;
		text-decoration: none;
		color: #0d6efd;
		transition: background .15s, border-color .15s;
	}

	.attachment-card:hover {
		background: #f0f6ff;
		border-color: #0d6efd;
	}

	.attachment-name {
		font-weight: 500;
	}

	.pre-retrieve {
		text-align: center;
	}

	.memo {
		margin: 0 0 1rem;
		padding: .75rem 1rem;
		border-left: 3px solid #0d6efd;
		background: #f0f6ff;
		text-align: left;
		border-radius: .25rem;
	}

	.memo-label {
		display: block;
		font-size: 12px;
		text-transform: uppercase;
		letter-spacing: .04em;
		color: #6c757d;
		margin-bottom: .25rem;
	}

	.memo-text {
		margin: 0;
		font-style: italic;
		color: #212529;
	}

	:global(.spinner) {
		display: flex;
		justify-content: center;
	}

	.password-input {
		width: 300px;
		margin: 0 auto;
	}

	.reads-remaining {
		display: block;
		width: 100%;
		text-align: center;
		color: #6c757d;
		font-size: 13px;
		margin: .5rem 0 1rem;
	}

	.final-read-warning {
		max-width: 500px;
		margin: 1rem auto;
		text-align: left;
	}

	.lifecycle {
		display: grid;
		grid-template-columns: max-content 1fr;
		column-gap: 1rem;
		row-gap: .25rem;
		margin-top: 1.5rem;
		padding-top: 1rem;
		border-top: 1px solid #dee2e6;
		font-size: 13px;
		color: #6c757d;
	}

	.lifecycle dt {
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: .04em;
		font-size: 11px;
		padding-top: .1rem;
	}

	.lifecycle dd {
		margin: 0;
	}

	.lifecycle .destroyed {
		color: #dc3545;
		font-weight: 600;
	}
</style>